import { useState } from 'react';
import type { BoardSnapshot, ManualResolutionDraft } from '@nerds-at-play/contracts';
import { parseRoundResponse, roundAssistantPrompt } from './round-assistant.js';
export function RoundAssistantExchange({
  board,
  source,
  onFill,
}: {
  board: BoardSnapshot;
  source?: ManualResolutionDraft['externalAssistance'];
  onFill: (
    values: Record<string, string>,
    source: NonNullable<ManualResolutionDraft['externalAssistance']>,
  ) => void;
}) {
  const [prompt, setPrompt] = useState(() => source?.prompt ?? roundAssistantPrompt(board));
  const [response, setResponse] = useState(source?.response ?? '');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  return (
    <section className="team-assistant-exchange">
      <h3>Check this round with your chatbot</h3>
      <p>
        Copy this GM-private prompt into your chatbot, paste its JSON response, and fill the
        consequence fields below. Review and edit before approval; publication is a separate action.
      </p>
      <label>
        Round assessment prompt
        <textarea rows={10} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      </label>
      <button
        onClick={() => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(prompt);
              setError('');
              setStatus('Round prompt copied.');
            } catch {
              setError('Select the prompt and copy manually if clipboard access is unavailable.');
            }
          })();
        }}
      >
        Copy round prompt
      </button>

      <label>
        Paste round assessment response
        <textarea rows={10} value={response} onChange={(e) => setResponse(e.target.value)} />
      </label>
      <button
        onClick={() => {
          try {
            if (prompt.length > 70000) throw new Error('Keep the prompt below 70,000 characters.');
            const values = parseRoundResponse(response, board);
            onFill(values, { prompt, response });
            setError('');
            setStatus(
              'Consequence fields filled. Nothing saved, approved or published. Review below.',
            );
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Invalid response. Fields unchanged.');
            setStatus('');
          }
        }}
      >
        Fill consequence fields
      </button>
      {status && <p role="status">{status}</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
